import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type {
  DayView,
  POI,
  TripLeg,
  ZonedTimeString,
} from "@poi-planner/shared";
import { legPalette } from "../../theme/colors";
import { formatShortDay, formatTimeBudget } from "../../utils/format";
import { suggestStartTimes } from "../../utils/freeSlots";
import {
  addMinutes,
  zonedDateInZone,
  zonedFromWallTime,
  zonedTimeOfDayInZone,
} from "../../utils/zonedTime";
import { Button } from "../Button";
import { FormInput } from "../FormFields";
import { Modal } from "../Modal";

function legForDate(legs: TripLeg[], date: string): TripLeg | undefined {
  return legs.find((leg) => date >= leg.startDate && date <= leg.endDate);
}

export interface ScheduledPick {
  poiId: string;
  date: string;
  startTime: ZonedTimeString;
  /** Wall time, e.g. "10:00", for the confirmation toast. */
  timeLabel: string;
}

interface SchedulePoiPickerProps {
  poi: POI;
  dayViews: DayView[];
  legs: TripLeg[];
  /** The date focused in the Planner, used when it is inside the trip. */
  defaultDate: string | null;
  onClose: () => void;
  onConfirm: (pick: ScheduledPick) => void;
}

/**
 * Phone bottom sheet for scheduling a result without dragging: pick a day,
 * then a start time from that day's free gaps (or type one). The POI's time
 * budget is the duration.
 */
export function SchedulePoiPicker({
  poi,
  dayViews,
  legs,
  defaultDate,
  onClose,
  onConfirm,
}: SchedulePoiPickerProps) {
  const [date, setDate] = useState(
    () =>
      dayViews.find((day) => day.date === defaultDate)?.date ??
      dayViews[0]?.date ??
      "",
  );
  const [chosenTime, setChosenTime] = useState<string | null>(null);
  const day = dayViews.find((view) => view.date === date);
  const duration = poi.timeBudget || 60;
  const suggestions = useMemo(
    () => (day ? suggestStartTimes(day, duration) : []),
    [day, duration],
  );
  const time = chosenTime ?? suggestions[0] ?? "";
  const startTime =
    day && time ? zonedFromWallTime(date, time, day.zone) : null;
  const endTime = startTime ? addMinutes(startTime, duration) : null;
  const endsNextDay =
    day && endTime ? zonedDateInZone(endTime, day.zone) !== date : false;

  // Bring the chosen day into view, including the default on open
  const dayRowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dayRowRef.current
      ?.querySelector<HTMLElement>('[aria-pressed="true"]')
      ?.scrollIntoView?.({ inline: "center", block: "nearest" });
  }, [date]);

  const selectDate = (next: string) => {
    setDate(next);
    setChosenTime(null);
  };

  return (
    <Modal
      open
      onClose={onClose}
      placement="sheet"
      title={poi.name}
      subtitle={
        <p className="m-0 text-xs text-secondary">
          Schedule · {formatTimeBudget(duration)}
        </p>
      }
      footer={
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!startTime}
          onClick={() => {
            if (!startTime) return;
            onConfirm({ poiId: poi.id, date, startTime, timeLabel: time });
          }}
        >
          Add to {formatShortDay(date)}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <section aria-labelledby="schedule-day">
          <h4 id="schedule-day" className="form-label">
            Day
          </h4>
          <div
            ref={dayRowRef}
            className="scrollbar-none -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6"
            role="group"
            aria-labelledby="schedule-day"
          >
            {dayViews.map((view) => {
              const leg = legForDate(legs, view.date);
              const active = view.date === date;
              const label = new Date(`${view.date}T00:00:00`);
              return (
                <Button
                  key={view.date}
                  variant="unstyled"
                  aria-pressed={active}
                  aria-label={`${formatShortDay(view.date)}${leg ? `, ${leg.name}` : ""}`}
                  data-autofocus={active ? "" : undefined}
                  onClick={() => selectDate(view.date)}
                  className={clsx(
                    "flex min-h-14 w-12 shrink-0 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-xl border text-xs transition-colors",
                    active
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-surface text-primary",
                  )}
                >
                  <span className="font-medium opacity-75">
                    {label.toLocaleDateString("en-US", { weekday: "short" })}
                  </span>
                  <span className="text-sm font-bold tabular-nums">
                    {label.getDate()}
                  </span>
                  <span
                    aria-hidden
                    className="size-1.5 rounded-full"
                    // Runtime palette value: the date's leg colour
                    style={{
                      backgroundColor: leg
                        ? legPalette(leg.color).dot
                        : "transparent",
                    }}
                  />
                </Button>
              );
            })}
          </div>
        </section>

        <section aria-labelledby="schedule-time">
          <h4 id="schedule-time" className="form-label">
            Start time
          </h4>
          {suggestions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion) => (
                <Button
                  key={suggestion}
                  variant="unstyled"
                  aria-pressed={suggestion === time}
                  onClick={() => setChosenTime(suggestion)}
                  className={clsx(
                    "min-h-11 cursor-pointer rounded-full border px-4 text-sm font-semibold tabular-nums transition-colors",
                    suggestion === time
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-surface text-primary",
                  )}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          ) : (
            <p className="m-0 text-xs text-tertiary">
              No free {formatTimeBudget(duration)} on this day. Pick a time.
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <label
              htmlFor="schedule-custom-time"
              className="text-xs font-semibold text-secondary"
            >
              Custom
            </label>
            <FormInput
              id="schedule-custom-time"
              type="time"
              value={time}
              onChange={(value) => setChosenTime(value || null)}
              className="w-32 tabular-nums"
            />
          </div>
        </section>

        <p className="m-0 text-sm text-primary" aria-live="polite">
          {startTime && endTime && day ? (
            <>
              <span className="font-semibold tabular-nums">
                {zonedTimeOfDayInZone(startTime, day.zone)} –{" "}
                {zonedTimeOfDayInZone(endTime, day.zone)}
              </span>
              {endsNextDay && " (next day)"} · {formatShortDay(date)}
            </>
          ) : (
            <span className="text-tertiary">Choose a start time</span>
          )}
        </p>
      </div>
    </Modal>
  );
}
